import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog.tsx';
import { Button } from '@/components/ui/button.tsx';
import { Input } from '@/components/ui/input.tsx';
import { Label } from '@/components/ui/label.tsx';
import { Checkbox } from '@/components/ui/checkbox.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Eye, EyeOff, Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@/lib/utils.ts';
import { useCareSpecialistDialog } from '@/hooks/ca/useCareSpecialistDialog.ts';

interface CareSpecialistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  careSpecialist?: any; // For edit mode
  isEditMode?: boolean;
}

const CareSpecialistDialog: React.FC<CareSpecialistDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  careSpecialist,
  isEditMode = false
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [clinicSearchOpen, setClinicSearchOpen] = useState(false);
  const [clinicSearch, setClinicSearch] = useState('');

  // Use the custom hook for business logic
  const {
    formData,
    setFormData,
    errors,
    isLoading,
    clinics,
    handleSubmit,
    handleClinicToggle,
    removeSelectedClinic,
    resetForm,
    clearFieldError
  } = useCareSpecialistDialog(open, careSpecialist, isEditMode);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await handleSubmit();
    if (success) {
      onOpenChange(false);
      onSuccess();
    }
  };

  const handleCancel = () => {
    resetForm();
    setClinicSearch('');
    onOpenChange(false);
  };

  const filteredClinics = clinics.filter(clinic =>
    clinic.clinic_name.toLowerCase().includes(clinicSearch.toLowerCase())
  );

  const selectedClinics = clinics.filter(c => formData.selectedClinicIds.includes(c.clinic_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" data-testid="care-specialist-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-center" data-testid="care-specialist-dialog-title">
            {isEditMode ? 'Update Care Specialist Details' : 'Add Care Specialist'}
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleFormSubmit} className="space-y-4" data-testid="care-specialist-form">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name <span className="text-red-500">*</span></Label>
              <Input
                id="firstName"
                placeholder="Enter First Name"
                value={formData.firstName}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, firstName: e.target.value }));
                  clearFieldError('firstName');
                }}
                className={errors.firstName ? 'border-red-500' : ''}
                autoComplete="off"
                data-testid="care-specialist-first-name-input"
              />
              {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name <span className="text-red-500">*</span></Label>
              <Input
                id="lastName"
                placeholder="Enter Last Name"
                value={formData.lastName}
                onChange={(e) => {
                  setFormData(prev => ({ ...prev, lastName: e.target.value }));
                  clearFieldError('lastName');
                }}
                className={errors.lastName ? 'border-red-500' : ''}
                autoComplete="off"
                data-testid="care-specialist-last-name-input"
              />
              {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter Email Address"
              value={formData.email}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, email: e.target.value }));
                clearFieldError('email');
              }}
              className={errors.email ? 'border-red-500' : ''}
              autoComplete="new-email"
              data-testid="care-specialist-email-input"
            />
            {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
          </div>

          <div className="space-y-3" data-testid="care-specialist-tasks-section">
            <Label>Assigned Tasks :</Label>
            <div className="flex space-x-6">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="deferred"
                  checked={formData.deferredTask}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, deferredTask: checked as boolean }))
                  }
                  data-testid="care-specialist-deferred-checkbox"
                />
                <Label htmlFor="deferred">Deferred</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="followUp"
                  checked={formData.followUpTask}
                  onCheckedChange={(checked) => 
                    setFormData(prev => ({ ...prev, followUpTask: checked as boolean }))
                  }
                  data-testid="care-specialist-followup-checkbox"
                />
                <Label htmlFor="followUp">Follow Up</Label>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4" data-testid="care-specialist-password-section">
            <div className="space-y-2">
              <Label htmlFor="password">
                Password {!isEditMode && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={isEditMode ? "Leave blank to keep current" : "Enter Password"}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, password: e.target.value }));
                    clearFieldError('password');
                  }}
                  className={errors.password ? 'border-red-500' : ''}
                  autoComplete="new-password"
                  data-testid="care-specialist-password-input"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {isEditMode && !errors.password && (
                <p className="text-gray-500 text-xs">Leave blank to keep current password</p>
              )}
              {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                Confirm Password {!isEditMode && <span className="text-red-500">*</span>}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
                    clearFieldError('confirmPassword');
                  }}
                  className={errors.confirmPassword ? 'border-red-500' : ''}
                  autoComplete="new-password"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
              {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="clinic">Select Clinic <span className="text-red-500">*</span></Label>
            
            {/* Selected Clinics Display */}
            {selectedClinics.length > 0 && (
              <div className="flex flex-wrap gap-2 p-3 border rounded-md bg-gray-50">
                {selectedClinics.map((clinic) => (
                  <div
                    key={clinic.clinic_id}
                    className="flex items-center space-x-2 bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm"
                  >
                    <span>{clinic.clinic_name}</span>
                    <button
                      type="button"
                      onClick={() => removeSelectedClinic(clinic.clinic_id)}
                      className="hover:bg-blue-200 rounded-full p-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <Popover open={clinicSearchOpen} onOpenChange={setClinicSearchOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clinicSearchOpen}
                  className={cn(
                    "w-full justify-between",
                    errors.clinics ? 'border-red-500' : ''
                  )}
                >
                  Select clinics...
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-full p-0">
                <div className="p-3">
                  <Input
                    placeholder="Search clinics..."
                    value={clinicSearch}
                    onChange={(e) => setClinicSearch(e.target.value)}
                    className="mb-3"
                  />
                  <div className="max-h-48 overflow-y-auto">
                    {filteredClinics.length === 0 ? (
                      <p className="p-2 text-sm text-gray-500">No clinics found.</p>
                    ) : (
                      filteredClinics.map((clinic) => (
                        <div
                          key={clinic.clinic_id}
                          className="flex items-center space-x-2 p-2 hover:bg-gray-100 cursor-pointer"
                          onClick={() => handleClinicToggle(clinic.clinic_id)}
                        >
                          <div className="flex items-center space-x-2 w-full">
                            <div className={cn(
                              "w-4 h-4 border rounded flex items-center justify-center",
                              formData.selectedClinicIds.includes(clinic.clinic_id)
                                ? "bg-blue-600 border-blue-600"
                                : "border-gray-300"
                            )}>
                              {formData.selectedClinicIds.includes(clinic.clinic_id) && (
                                <Check className="w-3 h-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="text-sm font-medium">{clinic.clinic_name}</div>
                              <div className="text-xs text-gray-500">{clinic.patient_clinic_timezone}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </PopoverContent>
            </Popover>
            {errors.clinics && <p className="text-red-500 text-sm">{errors.clinics}</p>}
          </div>

          <div className="flex space-x-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"
              disabled={isLoading}
            >
              {isLoading ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update" : "Create")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CareSpecialistDialog;